/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-nested-ternary */
import React, { useEffect, useState, useContext } from 'react'

import { TextField, Typography, Grid, Tooltip } from '@material-ui/core'
import Breadcrumbs from '@material-ui/core/Breadcrumbs'
import { makeStyles } from '@material-ui/core/styles'
import NavigateNextIcon from '@material-ui/icons/NavigateNext'

import BackDrop from 'components/BackDrop'
import CategorySuggestions from 'components/CategorySuggestions'
import Button from 'components/CustomButtons/Button'
import StyledRadio from 'components/StyledRadio'
import {
  PRODUCT_CONDITIONS,
  PRODUCT_TYPES,
  SIMPLE_PRODUCT,
  VARIABLE_PRODUCT,
  RECHARGABLE_PRODUCT,
  VIRTUAL_PRODUCTS_TYPES,
} from 'constants/productsConstants'
import { SUPER_ADMIN } from 'constants/rolesConst'
import useAuthContext from 'contextApi/AuthContext'
import { useProduct } from 'contextApi/ProductContext'

const useStyles = makeStyles((theme) => ({
  tooltipPlacementTop: {
    fontSize: '12px',
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.primary,
    boxShadow: '-6px 6px #E8E8E8',
  },
  root: {
    margin: '0 10%',
  },
  buttonsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  button: {
    width: '248px',
    height: '40px',
    borderRadius: '50px',
    border: 'none',
    margin: '20px 0',
    backgroundColor: '#6064FF',
    color: 'white',
    fontWeight: '500',
    '&:hover': {
      fontWeight: 'bolder',
      backgroundColor: '#ef0bef',
      boxShadow: '-7px 7px 0px #000000',
      border: '2px solid #000000',
      borderRadius: '50px',
    },
  },
  buttonText: {
    color: '#6064FF',
    fontWeight: '500',
    '&:hover': {
      fontWeight: 'bolder',
      color: '#ef0bef',
    },
  },
  optionsBox: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '15px',
    marginTop: '15px',
  },
  tooltipWrapper: {
    display: 'inline-flex',
  },
}))

const ProductCategorization = () => {
  const { product, setProduct, handleNext } = useProduct()
  const { userLoggedin, rechargableProductSellerIds } = useContext(useAuthContext)
  const classes = useStyles()
  const [isLoading, setIsLoading] = useState(false)
  const [productName, setProductName] = useState('')
  const [productType, setProductType] = useState('')
  const [productCondition, setProductCondition] = useState({})
  const [categories, setCategories] = useState([])
  const [isOpen, setIsOpen] = useState(false)
  const [hasAutomaticConditionReset, setHasAutomaticConditionReset] = useState(false)
  const NEW_CONDITION = PRODUCT_CONDITIONS[0]?.value
  const RECONDITIONED_CATEGORY_ID = 12808
  const hasSelectedCategory = categories.length > 0

  const isReconditionedEnabled =
    !hasSelectedCategory ||
    categories.some((category) => Number(category?.id) === RECONDITIONED_CATEGORY_ID)
  const isReconditionedBlockedByCategory = hasSelectedCategory && !isReconditionedEnabled
  const restrictionMessage = hasAutomaticConditionReset
    ? 'La condición Reacondicionado solo está disponible para la categoría Celulares y Smartphones. Se seleccionó automáticamente la condición Nuevo.'
    : 'La condición Reacondicionado solo está disponible para la categoría Celulares y Smartphones.'

  const availableProductTypes = React.useMemo(() => {
    const isSuperAdmin = userLoggedin?.role === SUPER_ADMIN
    const isAllowedSeller = rechargableProductSellerIds.includes(userLoggedin?.sellerId)

    if (!isSuperAdmin && !isAllowedSeller) {
      return PRODUCT_TYPES.filter((type) => type.value !== RECHARGABLE_PRODUCT)
    }

    return PRODUCT_TYPES
  }, [userLoggedin?.role, userLoggedin?.sellerId, rechargableProductSellerIds])

  const handleChangeProductType = (value) => {
    setProductType(value)
  }

  const handleChangeProductCondition = (value) => {
    setProductCondition(value)
  }

  const handleFinishStep = () => {
    const conditionToSave = isReconditionedEnabled ? productCondition : NEW_CONDITION
    const productData = {
      ...product,
      name: productName,
      type: productType,
      categories,
    }

    if ([SIMPLE_PRODUCT, VARIABLE_PRODUCT].includes(productType)) {
      productData.isReconditioned = conditionToSave?.isReconditioned
    } else {
      delete productData.isReconditioned
      productData.stockQuantity = VIRTUAL_PRODUCTS_TYPES.includes(productType)
        ? product.stockQuantity ?? 0
        : 1000
      productData.dimensions = {
        height: '0',
        length: '0',
        width: '0',
        weight: '0',
      }
      if (productType === RECHARGABLE_PRODUCT) {
        productData.rangePrice = {
          minPrice: product.rangePrice?.minPrice ?? 10000,
          maxPrice: product.rangePrice?.maxPrice ?? 990000,
        }
        productData.regularPrice = product.rangePrice?.minPrice ?? 10000
      }
    }
    setProduct(productData)
    handleNext()
  }

  useEffect(() => {
    setProductName(product.name)
    setProductType(product.type)
    if ([SIMPLE_PRODUCT, VARIABLE_PRODUCT].includes(product.type)) {
      const selectedCondition =
        PRODUCT_CONDITIONS.find((item) => item.value.isReconditioned === product.isReconditioned)
          ?.value ?? PRODUCT_CONDITIONS[0].value
      setProductCondition(selectedCondition)
    } else {
      setProductCondition(null)
    }
    setCategories(product.categories)
  }, [product.name, product.type, product.isReconditioned, product.categories])

  useEffect(() => {
    if (
      [SIMPLE_PRODUCT, VARIABLE_PRODUCT].includes(productType) &&
      hasSelectedCategory &&
      !isReconditionedEnabled &&
      productCondition?.isReconditioned
    ) {
      setProductCondition(NEW_CONDITION)
      setHasAutomaticConditionReset(true)
    }
  }, [productType, hasSelectedCategory, isReconditionedEnabled, productCondition])

  useEffect(() => {
    if (!isReconditionedBlockedByCategory && hasAutomaticConditionReset) {
      setHasAutomaticConditionReset(false)
    }
  }, [isReconditionedBlockedByCategory, hasAutomaticConditionReset])

  const stepCompleted =
    !productName ||
    !productType ||
    ([SIMPLE_PRODUCT, VARIABLE_PRODUCT].includes(productType) && !productCondition) ||
    categories.length === 0

  return (
    <div className={classes.root}>
      <BackDrop open={isLoading} />
      <Typography variant="subtitle1">Tipo de producto</Typography>
      <div className={classes.optionsBox}>
        {availableProductTypes.map((item) => (
          <StyledRadio
            key={item.value}
            label={item.label}
            description={item.description}
            disabled={product.id !== '0'}
            value={item.value}
            selected={productType === item.value}
            handleChange={handleChangeProductType}
            icon={item.icon(productType === item.value, product.id !== '0')}
          />
        ))}
      </div>
      <br />
      {[SIMPLE_PRODUCT, VARIABLE_PRODUCT].includes(productType) && (
        <>
          <div>
            <Typography variant="subtitle1">Condición de tu producto</Typography>
            <div className={classes.optionsBox}>
              {PRODUCT_CONDITIONS.map((item) => {
                const isConditionDisabled =
                  product.id !== '0' || (!isReconditionedEnabled && item.value.isReconditioned)
                const shouldShowTooltip = item.value.isReconditioned && isConditionDisabled

                const conditionOption = (
                  <StyledRadio
                    key={item.label}
                    label={item.label}
                    value={item.value}
                    selected={productCondition === item.value}
                    handleChange={handleChangeProductCondition}
                    icon={item.icon(productCondition === item.value, isConditionDisabled)}
                    disabled={isConditionDisabled}
                  />
                )

                if (!shouldShowTooltip) {
                  return conditionOption
                }

                return (
                  <Tooltip
                    key={item.label}
                    title={restrictionMessage}
                    interactive
                    placement="top"
                    classes={{
                      tooltipPlacementTop: classes.tooltipPlacementTop,
                    }}
                  >
                    <span className={classes.tooltipWrapper}>{conditionOption}</span>
                  </Tooltip>
                )
              })}
            </div>
          </div>
          <br />
        </>
      )}
      <Typography variant="subtitle1">Información Básica</Typography>
      <br />
      <Typography variant="paragraph">Nombre del producto:</Typography>
      <TextField
        fullWidth
        id="name"
        name="name"
        variant="outlined"
        placeholder="Camiseta blanca, manga corta, sin estampados..."
        value={productName}
        onChange={(e) => {
          setProductName(e.target.value)
          if (categories.length > 0) {
            setCategories([])
          }
        }}
      />
      {categories.length > 0 && productName.length > 0 ? (
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
          {categories.map((category) => (
            <Typography key={category.name} variant="paragraph" color="textPrimary">
              {category.name}
            </Typography>
          ))}
          <Button
            className={classes.buttonText}
            onClick={() => {
              setIsOpen(true)
            }}
          >
            Cambiar categoría
          </Button>
        </Breadcrumbs>
      ) : (
        <Typography variant="paragraph" color="inherit">
          Por favor, presione en "Categorizar" para ver las categorías sugeridas para este producto,
          o en su defecto seleccionarla manualmente
        </Typography>
      )}
      <Grid container justifyContent="center">
        <Button
          className={classes.button}
          onClick={() => {
            if (categories.length > 0) {
              handleFinishStep()
            } else {
              setIsOpen(true)
            }
          }}
          disabled={categories.length > 0 ? stepCompleted : productName.length < 3}
        >
          {categories.length > 0 ? 'Continuar' : 'Categorizar'}
        </Button>
      </Grid>
      <CategorySuggestions
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        name={productName}
        setIsLoading={setIsLoading}
        onFinish={(category) => {
          setCategories(category)
        }}
      />
    </div>
  )
}

export default ProductCategorization
